import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import { 
  FileText, Cpu, Shield, Coins, HelpCircle, ArrowRight, Check, CheckCircle, 
  Download, Copy, Database, ExternalLink, Zap, Landmark, Award, Eye, Trash2, Edit2,
  Printer, X
} from "lucide-react";
import { CATEGORIES } from "../data";
import { useToast } from "../contexts/ToastContext";
import { TruthRegistry } from "../lib/NexoraxTruthRegistry";

interface Proposal {
  title: string;
  solution: string;
  stack: string;
  roi_projection: string;
  investment: number;
  deposit: number;
  levy: number;
  tax: number;
  netSecurePaymentSettlement: number;
  charity1Alloc: number;
  charity2Alloc: number;
  charity3Alloc: number;
}

export function NexoraXEnterpriseProposalAutomator() {
  const { showToast } = useToast();
  
  // Mandatory Preamble
  const preamble = "In the name of Allah, the Greatest and Most Merciful. Praise be to Allah, Lord of the Worlds.";

  const [showPdfReport, setShowPdfReport] = useState<boolean>(false);

  const triggerPrint = () => {
    document.body.classList.add("printing-active-proposal");
    setTimeout(() => {
      window.print();
      document.body.classList.remove("printing-active-proposal");
    }, 200);
  };

  // Form State
  const [selectedIndustry, setSelectedIndustry] = useState<string>("Dental Clinic");
  const [customIndustry, setCustomIndustry] = useState<string>("");
  const [serviceType, setServiceType] = useState<string>("Cloud Data Migration");
  const [painPoint, setPainPoint] = useState<string>("Patient Record Bottlenecks");
  const [investmentValue, setInvestmentValue] = useState<number>(10000);
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [currentProposal, setCurrentProposal] = useState<Proposal | null>(null);
  
  // List of generated proposals history (stored in localStorage)
  const [proposalsHistory, setProposalsHistory] = useState<Proposal[]>([]);

  // Search filtering for the 90+ industries
  const [industrySearch, setIndustrySearch] = useState<string>("");

  useEffect(() => {
    try {
      const savedHistory = localStorage.getItem("nexorax_proposals_history");
      if (savedHistory) {
        setProposalsHistory(JSON.parse(savedHistory));
      }
    } catch (e) {
      console.warn("Failed to retrieve proposals history:", e);
    }
  }, []);

  const saveHistory = (newHistory: Proposal[]) => {
    setProposalsHistory(newHistory);
    try {
      localStorage.setItem("nexorax_proposals_history", JSON.stringify(newHistory));
    } catch (e) {
      console.warn("Failed to write proposals history:", e);
    }
  };

  // 1% 3% Community Allocation & 10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee) extraction model
  const generateProposal = () => {
    if (!selectedIndustry && !customIndustry) {
      showToast("Select or input a target client industry sector first.", "warning");
      return;
    }

    setIsGenerating(true);
    
    setTimeout(() => {
      const finalIndustry = customIndustry.trim() || selectedIndustry;
      const amount = investmentValue;
      
      // Multi-stage Tax Accounting Logic (Tiered Extraction Protocol)
      const levyTotal = amount * 0.01;
      const splitCharity1 = levyTotal / 3; // Dunfermline
      const splitCharity2 = levyTotal / 3; // Global
      const splitCharity3 = levyTotal / 3; // UK
      
      const netAfterLevy = amount - levyTotal;
      const gavelFee = netAfterLevy * 0.20;
      const netSecurePaymentSettlement = netAfterLevy - gavelFee;
      
      // Deposit standard is 5% representation of the architecture setup phase
      const depositAmount = amount * 0.05;

      const newProposal: Proposal = {
        title: `NexoraX NexoraX Integration: ${finalIndustry.toUpperCase()}`,
        solution: `Automated ${serviceType} to solve ${painPoint}`,
        stack: "Google Cloud / Firebase / NexoraX AI Core",
        roi_projection: "35-50% efficiency gain within 90 days",
        investment: amount,
        deposit: depositAmount,
        levy: levyTotal,
        tax: gavelFee,
        netSecurePaymentSettlement: netSecurePaymentSettlement,
        charity1Alloc: splitCharity1,
        charity2Alloc: splitCharity2,
        charity3Alloc: splitCharity3,
      };

      setCurrentProposal(newProposal);
      const updatedHistory = [newProposal, ...proposalsHistory.slice(0, 19)];
      saveHistory(updatedHistory);
      
      // Log the projection into TruthRegistry as a pending projection
      try {
        TruthRegistry.logVerifiedRevenue(0, "Bank Transfer", `Projected Proposal: ${newProposal.title}`);
      } catch (err) {
        console.warn("Registry log omitted:", err);
      }

      setIsGenerating(false);

      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast("NexoraX Enterprise Proposal Compiled successfully.", "success");
      } else {
        showToast("NexoraX Enterprise Proposal Compiled successfully.", "success");
      }
    }, 700);
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast("NexoraX mandate text copied to clipboard successfully.", "success");
  };

  const triggerCheckout = (prop: Proposal) => {
    if (typeof (window as any).triggerNexoraXPayment === 'function') {
      (window as any).triggerNexoraXPayment({
        amount: prop.deposit,
        clientName: prop.title,
        invoiceRef: `NX-PROP-${Math.floor(1000 + Math.random() * 9000)}`,
        channel: 'SecurePayment'
      });
      if (typeof (window as any).showCosmicToast === 'function') {
        (window as any).showCosmicToast(`Initialized deposit checkout for £${prop.deposit}`, 'success');
      }
    } else {
      showToast("Payment integration offline. Scroll down manually to payment deck.", "warning");
    }
  };

  // Filter 90+ categories
  const filteredCategories = CATEGORIES.filter(cat => 
    cat.title.toLowerCase().includes(industrySearch.toLowerCase()) ||
    cat.nodes.some(n => n.toLowerCase().includes(industrySearch.toLowerCase()))
  );

  return (
    <div className="w-full max-w-7xl mx-auto space-y-10 relative z-10 p-4 sm:p-6 lg:p-8 select-none">
      
      {/* Visual Header */}
      <div className="text-center space-y-3 max-w-4xl mx-auto">
        <div className="text-[10px] sm:text-xs font-mono font-extrabold tracking-[0.25em] text-cyan-400 uppercase select-all">
          {preamble}
        </div>
        
        <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-cyan-500/30 to-transparent" />
        
        <h1 className="text-3xl sm:text-4xl font-sans font-black tracking-widest text-white uppercase drop-shadow-[0_0_15px_rgba(6,182,212,0.3)]">
          ENTERPRISE PROPOSAL AUTOMATOR
        </h1>
        <p className="text-[10px] font-mono tracking-[0.3em] text-slate-500 uppercase">
          Node-21 Architectural Core // Lead Director Office
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* INPUT PANEL (Col-span 5) */}
        <div className="lg:col-span-5 space-y-6">
          <div className="bg-[#02050f]/80 border border-cyan-500/20 rounded-3xl p-6 shadow-[0_0_20px_rgba(6,182,212,0.05)] backdrop-blur-md relative overflow-hidden">
            <div className="absolute top-0 left-0 w-1.5 h-full bg-cyan-500/60" />
            
            <div className="flex items-center gap-2 border-b border-cyan-900/40 pb-3 mb-6">
              <Cpu className="w-5 h-5 text-cyan-400 animate-pulse" />
              <h3 className="text-sm font-mono font-bold text-white uppercase tracking-wider">
                NexoraX System Inputs
              </h3>
            </div>

            <div className="space-y-5">
              
              {/* Category Search & Dropdown Selection */}
              <div className="space-y-2">
                <label className="block text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">
                  Target client industry / Category (Preloads 90+ categories)
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={industrySearch}
                    onChange={(e) => setIndustrySearch(e.target.value)}
                    placeholder="Search 90+ pre-mapped industry frameworks..."
                    className="w-full bg-[#050914] border border-cyan-500/20 hover:border-cyan-500/40 focus:border-cyan-500 px-4 py-2.5 rounded-lg text-xs font-mono text-cyan-100 placeholder-slate-600 focus:outline-none transition-all"
                  />
                </div>
                
                <div className="h-32 overflow-y-auto border border-cyan-500/10 rounded-lg bg-[#040712] p-1.5 space-y-1 custom-scrollbar">
                  {filteredCategories.map((cat) => (
                    <button
                      key={cat.id}
                      type="button"
                      onClick={() => {
                        setSelectedIndustry(cat.title);
                        setCustomIndustry("");
                        if (cat.nodes.length > 0) {
                          setServiceType(cat.nodes[0]);
                        }
                      }}
                      className={`w-full text-left font-mono text-[10px] px-2.5 py-1.5 rounded transition-all flex items-center justify-between ${
                        selectedIndustry === cat.title && !customIndustry
                          ? "bg-cyan-500/20 text-cyan-400 font-bold border-l-2 border-cyan-400"
                          : "text-slate-400 hover:bg-cyan-950/20 hover:text-cyan-300"
                      }`}
                    >
                      <span className="truncate uppercase">{cat.title}</span>
                      <span className="text-[8px] text-slate-600 px-1 border border-slate-800 rounded shrink-0">
                        {cat.nodes.length} Nodes
                      </span>
                    </button>
                  ))}
                  {filteredCategories.length === 0 && (
                    <div className="text-[10px] text-slate-600 italic p-4 text-center font-mono">
                      No matching integrated nodes. Use absolute override below.
                    </div>
                  )}
                </div>
              </div>

              {/* Absolute industry override */}
              <div className="space-y-2">
                <label className="block text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">
                  Absolute Override (Self-Defined Custom Niche/Industry)
                </label>
                <input
                  type="text"
                  value={customIndustry}
                  onChange={(e) => {
                    setCustomIndustry(e.target.value);
                    setSelectedIndustry("");
                  }}
                  placeholder="e.g. Dental Clinic, Real Estate Agency, etc."
                  className="w-full bg-[#040712] border border-cyan-500/20 focus:border-cyan-500 px-3.5 py-2.5 rounded-lg text-xs font-mono text-cyan-100 placeholder-slate-600 focus:outline-none transition-all"
                />
              </div>

              {/* Service Type */}
              <div className="space-y-2">
                <label className="block text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">
                  Automated service Type (Core Solution Focus)
                </label>
                <input
                  type="text"
                  value={serviceType}
                  onChange={(e) => setServiceType(e.target.value)}
                  placeholder="e.g. Cloud Data Migration or Voice Matrix integration"
                  className="w-full bg-[#040712] border border-cyan-500/20 focus:border-cyan-400 px-3.5 py-2.5 rounded-lg text-xs font-mono text-cyan-100 placeholder-slate-600 focus:outline-none transition-all"
                />
              </div>

              {/* Pain Point */}
              <div className="space-y-2">
                <label className="block text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">
                  Target client Pain Point
                </label>
                <input
                  type="text"
                  value={painPoint}
                  onChange={(e) => setPainPoint(e.target.value)}
                  placeholder="e.g. Patient Record Bottlenecks or High Administrative Overhead"
                  className="w-full bg-[#040712] border border-cyan-500/20 focus:border-cyan-400 px-3.5 py-2.5 rounded-lg text-xs font-mono text-cyan-100 placeholder-slate-600 focus:outline-none transition-all"
                />
              </div>

              {/* Investment slider */}
              <div className="space-y-2 pt-2">
                <div className="flex items-center justify-between text-[10px] font-mono font-bold uppercase tracking-wider text-slate-400">
                  <span>Investment Tier Target</span>
                  <span className="text-cyan-400 font-extrabold text-sm">£{investmentValue.toLocaleString()}</span>
                </div>
                <input
                  type="range"
                  min="5000"
                  max="15000"
                  step="500"
                  value={investmentValue}
                  onChange={(e) => setInvestmentValue(parseInt(e.target.value))}
                  className="w-full accent-cyan-500 h-1 bg-cyan-950 rounded-lg cursor-pointer transition-all"
                />
                <div className="flex justify-between text-[8px] font-mono text-slate-500 uppercase">
                  <span>£5,000 (Base)</span>
                  <span>£10,000 (Optimized)</span>
                  <span>£15,000 (Absolute Core)</span>
                </div>
              </div>

              <button
                type="button"
                onClick={generateProposal}
                disabled={isGenerating}
                className="w-full mt-4 flex items-center justify-center gap-2.5 py-3 px-4 bg-gradient-to-r from-cyan-600 to-cyan-500 hover:from-cyan-500 hover:to-cyan-400 disabled:opacity-40 text-black font-sans font-black tracking-widest text-xs uppercase rounded-xl shadow-[0_0_20px_rgba(6,182,212,0.3)] hover:shadow-[0_0_25px_rgba(6,182,212,0.5)] transition-all duration-300 relative overflow-hidden group cursor-pointer"
              >
                <Zap className="w-4 h-4 animate-bounce text-black" />
                <span>{isGenerating ? "COMPILING SYSTEM LOGS..." : "GENERATE PROPOSAL"}</span>
              </button>

            </div>
          </div>
        </div>

        {/* OUTPUT PROPOSAL PREVIEW PANEL (Col-span 7) */}
        <div className="lg:col-span-7 flex flex-col justify-between space-y-6">
          <div className="bg-[#02050f]/90 border border-cyan-500/20 rounded-3xl p-6 sm:p-8 flex flex-col justify-between h-full space-y-6 relative shadow-[0_0_30px_rgba(0,0,0,0.5)] backdrop-blur-md">
            
            {/* System Interlock Badge */}
            <div className="absolute top-4 right-4 font-mono text-[8px] sm:text-[9px] text-cyan-400/60 uppercase tracking-widest flex items-center gap-1.5 select-none font-bold">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block animate-ping" />
              <span>ACTIVE HANDSHAKE COMPLIANCE</span>
            </div>

            <div className="space-y-6 flex-1">
              <div className="border-b border-cyan-500/20 pb-3 flex items-center gap-2">
                <FileText className="w-5 h-5 text-cyan-400" />
                <div className="text-cyan-400 font-black text-xs uppercase tracking-[0.25em] font-mono">
                  NexoraX Proposal Manifest
                </div>
              </div>

              {currentProposal ? (
                <div className="space-y-6 text-left">
                  
                  {/* Generated Box with Gold Theme */}
                  <div className="bg-[#040814] border border-amber-500/30 rounded-2xl p-5 sm:p-6 space-y-5 shadow-[0_0_25px_rgba(245,158,11,0.06)] relative overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-gradient-to-br from-amber-500/10 to-transparent pointer-events-none rounded-bl-full" />
                    
                    <div className="space-y-1">
                      <span className="text-[9px] font-mono font-black text-amber-400 uppercase tracking-widest">
                        Official Proposed Document:
                      </span>
                      <h4 className="text-base sm:text-lg font-sans font-black tracking-wider text-white uppercase pr-10">
                        {currentProposal.title}
                      </h4>
                    </div>

                    <div className="h-px bg-amber-500/20 w-full" />

                    {/* Bold Sequential Reporting Architecture */}
                    <div className="space-y-4 font-mono text-xs text-slate-300">
                      <p className="leading-relaxed">
                        <strong className="text-white text-[13px] block mb-1">1. NEXORAX ARCHITECTURAL METHODOLOGY:</strong>
                        The Director's Office decrees the provision of {currentProposal.solution} utilizing a high-velocity, low-latency framework designed specifically for cloud operations.
                      </p>

                      <p className="leading-relaxed">
                        <strong className="text-white text-[13px] block mb-1">2. TECHNOLOGY STACK EMBLEM:</strong>
                        The engineering payload compiles entirely under <span className="text-cyan-300 font-bold">{currentProposal.stack}</span>, bypassing legacy infrastructure with an immutable 0.8s Handshake speed standard.
                      </p>

                      <p className="leading-relaxed">
                        <strong className="text-white text-[13px] block mb-1">3. FINANCIAL ALIGNMENT PROTOCOLS:</strong>
                        Total core investment is locked at <span className="text-emerald-400 font-black">£{currentProposal.investment.toLocaleString()}</span>. Under our absolute NexoraX mandates, a <span className="text-amber-400 font-bold">5% Architecture Initiation Fee of £{currentProposal.deposit.toLocaleString()}</span> is required immediately to book resource blocks.
                      </p>

                      <p className="leading-relaxed">
                        <strong className="text-white text-[13px] block mb-1">4. ROI STRATEGIC TIMELINES:</strong>
                        Target system operates under an expected <span className="text-cyan-400 font-black">{currentProposal.roi_projection}</span> with automatic failovers routed natively inside the Dunfermline NexoraX Resource Library.
                      </p>
                    </div>

                    {/* Tax & Extraction ledger */}
                    <div className="bg-black/90 rounded-xl p-4 border border-slate-900 space-y-3 font-mono text-[10px]">
                      <div className="text-cyan-400 font-extrabold tracking-widest uppercase border-b border-cyan-900/40 pb-1.5 flex items-center justify-between">
                        <span>Ledger Extraction Breakdown</span>
                        <Landmark className="w-3.5 h-3.5 text-cyan-500" />
                      </div>
                      
                      <div className="space-y-1.5 text-slate-400">
                        <div className="flex justify-between">
                          <span>1% 3% Community Allocation Split:</span>
                          <span className="text-white font-bold">£{currentProposal.levy.toLocaleString()}</span>
                        </div>
                        <div className="pl-3 space-y-1 text-slate-500 text-[9px] uppercase">
                          <div className="flex justify-between">
                            <span>- Charity 1 (Dunfermline):</span>
                            <span>£{currentProposal.charity1Alloc.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>- Charity 2 (Global):</span>
                            <span>£{currentProposal.charity2Alloc.toFixed(2)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span>- Charity 3 (UK):</span>
                            <span>£{currentProposal.charity3Alloc.toFixed(2)}</span>
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span>10% Maintenance & Hosting Fee (10% Maintenance & Hosting Fee):</span>
                          <span className="text-pink-400">£{currentProposal.tax.toLocaleString()}</span>
                        </div>
                        <div className="h-px bg-cyan-900/30 my-1" />
                        <div className="flex justify-between text-emerald-400 font-extrabold">
                          <span>Net Secure Payment Provider Settlement:</span>
                          <span>£{currentProposal.netSecurePaymentSettlement.toLocaleString()}</span>
                        </div>
                      </div>
                    </div>

                    <div className="border border-white/5 bg-[#030610] p-3 rounded-lg text-[9px] font-mono text-slate-500 uppercase leading-relaxed text-center">
                      IP NEXORAXTY SECURED. REPLICATION REPLICAS SYNCED LIVE TO GOOGLE DRIVE VIA CloudSync-02. All transactions route instantly to Sort: Verified Payment Configuration | A/C: Secure Billing Provider.
                    </div>

                  </div>

                  {/* Core Action triggers */}
                  <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => triggerCheckout(currentProposal)}
                      className="flex-1 min-w-[200px] flex items-center justify-center gap-2 py-3 px-5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black font-sans font-black tracking-widest text-xs uppercase rounded-xl shadow-[0_0_20px_rgba(245,158,11,0.25)] hover:shadow-[0_0_30px_rgba(245,158,11,0.4)] transition-all cursor-pointer"
                    >
                      <Coins className="w-4 h-4 text-black animate-spin" style={{ animationDuration: '6s' }} />
                      <span>Initiate Phase // Pay Deposit (£{currentProposal.deposit.toLocaleString()})</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => {
                        const template = `**${preamble}**\n\n**${currentProposal.title}**\n- **Core Mandate Solution:** ${currentProposal.solution}\n- **Engine NexoraX Stack:** ${currentProposal.stack}\n- **Guaranteed Projections:** ${currentProposal.roi_projection}\n- **NexoraX Investment:** £${currentProposal.investment.toLocaleString()}\n- **Dynamic Phase 1 Deposit (5%):** £${currentProposal.deposit.toLocaleString()}\n\n100% IP secured under Nexorax Digital Ltd. Net routing committed entirely to Secure Payment Provider.`;
                        copyToClipboard(template);
                      }}
                      className="py-3 px-4 border border-cyan-500/30 text-cyan-400 hover:text-white hover:bg-cyan-500/10 font-mono text-[10px] font-bold uppercase rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2"
                      title="Copy NexoraX mandate format to clipboard"
                    >
                      <Copy className="w-4 h-4" />
                      <span>Copy Mandate Text</span>
                    </button>

                    <button
                      type="button"
                      onClick={() => setShowPdfReport(true)}
                      className="py-3 px-4 border border-emerald-500/30 text-emerald-400 hover:text-white hover:bg-emerald-500/10 font-mono text-[10px] font-bold uppercase rounded-xl transition-all cursor-pointer flex items-center justify-center gap-2"
                      title="Generate and print official high fidelity PDF proposal report"
                    >
                      <Printer className="w-4 h-4" />
                      <span>Generate PDF Report</span>
                    </button>
                  </div>

                </div>
              ) : (
                <div className="h-full flex flex-col items-center justify-center text-center py-20 space-y-4">
                  <FileText className="w-12 h-12 text-slate-700 animate-pulse" />
                  <div className="space-y-1">
                    <p className="text-xs font-mono font-bold text-slate-500 uppercase tracking-widest">
                      &gt; Compiling parameters...
                    </p>
                    <p className="text-[10px] font-mono text-slate-600 max-w-sm uppercase">
                      Select client industry framework inputs and trigger generative compilation matrix to output high-fidelity architecture proposal details.
                    </p>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>

      </div>

      {/* HISTORICAL COMPILATIONS (FULL WIDTH) */}
      {proposalsHistory.length > 0 && (
        <div className="bg-[#02050f]/60 border border-slate-900 rounded-3xl p-6 space-y-4">
          <div className="flex items-center justify-between border-b border-slate-900 pb-2.5">
            <div className="flex items-center gap-2">
              <Database className="w-4 h-4 text-cyan-400" />
              <h4 className="text-xs font-mono font-bold text-white uppercase tracking-wider">
                Archive Log compiled via Chronos Ledger
              </h4>
            </div>
            <button
              onClick={() => {
                saveHistory([]);
                showToast("Proposal compilation archives cleared from sandbox logs.", "info");
              }}
              className="text-[10px] font-mono text-slate-500 hover:text-pink-400 transition-colors uppercase cursor-pointer"
            >
              Clear Logs
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {proposalsHistory.map((prop, idx) => (
              <div 
                key={idx} 
                className="bg-[#040814]/80 border border-cyan-500/10 hover:border-cyan-500/30 rounded-xl p-4 space-y-3 transition-all relative cursor-pointer group"
                onClick={() => {
                  setCurrentProposal(prop);
                  showToast(`Selected compiled proposal: ${prop.title}`, "info");
                }}
              >
                <div className="flex items-start justify-between gap-1">
                  <div className="space-y-0.5 truncate">
                    <span className="text-[8px] font-mono text-amber-500 uppercase tracking-widest block font-bold">
                      ARCHIVED MANDATE
                    </span>
                    <h5 className="text-[11px] font-sans font-black text-white uppercase truncate">
                      {prop.title}
                    </h5>
                    <p className="text-[9px] font-mono text-slate-400 truncate">
                      {prop.solution}
                    </p>
                  </div>
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500 shrink-0 opacity-60 group-hover:opacity-100 transition-opacity" />
                </div>

                <div className="flex items-center justify-between font-mono text-[9px] border-t border-slate-900 pt-2.5">
                  <span className="text-slate-500">Deposit Rate:</span>
                  <span className="text-amber-400 font-bold">£{prop.deposit.toLocaleString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* HIGH-FIDELITY PRINTABLE REPORT WRAPPER AND PREVIEW MODAL */}
      <AnimatePresence>
        {showPdfReport && currentProposal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99999] bg-black/95 backdrop-blur-md flex flex-col justify-start items-center p-4 sm:p-6 overflow-y-auto print-target-modal"
          >
            {/* Control Header */}
            <div className="w-full max-w-2xl bg-[#030611] border border-cyan-500/20 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row items-center justify-between gap-4 no-print shadow-[0_0_20px_rgba(6,182,212,0.1)]">
              <div className="flex items-center gap-3">
                <FileText className="w-5 h-5 text-cyan-400" />
                <div className="text-left">
                  <h4 className="text-xs font-mono font-bold text-white uppercase tracking-wider">NexoraX Document Compiler</h4>
                  <p className="text-[10px] font-mono text-slate-500 uppercase">Patent-Readiness Protected Vector Layout</p>
                </div>
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={triggerPrint}
                  className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-black font-sans font-black uppercase text-[10px] tracking-wider rounded-lg transition-all shadow-[0_0_15px_rgba(16,185,129,0.3)] cursor-pointer"
                >
                  <Printer className="w-4 h-4 text-black" />
                  <span>Print &bull; Save as PDF</span>
                </button>
                
                <button
                  type="button"
                  onClick={() => setShowPdfReport(false)}
                  className="p-2 border border-slate-800 hover:border-slate-500 text-slate-400 hover:text-white rounded-lg transition-all cursor-pointer"
                  title="Close Preview"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Document sheet representation Container */}
            <div 
              id="printable-pdf-document-area"
              className="w-full max-w-2xl bg-white text-slate-900 p-8 sm:p-14 rounded-xl border border-slate-300 shadow-2xl relative select-text text-[11px] leading-relaxed mx-auto print:max-w-none print:shadow-none print:border-none print:p-0 print:m-0"
              style={{ backgroundColor: "#ffffff" }}
            >
              {/* Decorative Hologram Seal on Screen, hidden in print */}
              <div className="absolute top-8 right-8 w-20 h-20 border border-slate-300 rounded-full flex flex-col items-center justify-center text-[8px] font-mono font-bold text-slate-500 uppercase p-1 text-center select-none tracking-tighter border-dashed animate-spin no-print" style={{ animationDuration: '40s' }}>
                <span>NEXORAX</span>
                <span>SECURED</span>
                <span>SC887095</span>
              </div>

              {/* PDF Document Header / Institutional Letterhead */}
              <div className="space-y-4 border-b border-slate-900 pb-6 mb-6">
                <div className="text-center space-y-1.5">
                  <span className="text-[7px] font-mono font-bold tracking-[0.3em] text-slate-500 uppercase block">
                    {preamble}
                  </span>
                  <p className="text-[14px] font-sans font-black tracking-[0.25em] text-black uppercase">
                    NEXORAX DIGITAL LTD
                  </p>
                  <p className="text-[8px] font-mono tracking-widest text-slate-500 uppercase">
                    SC887095 &bull; EDINBURGH &bull; ESTABLISHED CORPORATE CONTEXT
                  </p>
                </div>

                <div className="flex justify-between items-end text-[8px] font-mono text-slate-500 uppercase pt-2 border-t border-slate-200 text-left">
                  <div>
                    <span className="font-bold text-slate-800 block">ADMINISTRATIVE ANCHORS:</span>
                    <span>VOICE: 08000 622827 &bull; PLANET TELECOM</span>
                    <span className="block">EMAIL: SUPPORT@NEXORAXDIGITAL.CO.UK</span>
                  </div>
                  <div className="text-right">
                    <span className="font-bold text-slate-800 block text-[9px]">CONFIDENTIAL MANDATE DECREE</span>
                    <span>GENERATION UTCTIME: {new Date().toISOString()}</span>
                    <span className="block font-bold mt-0.5 text-black">REGID: NX-MANDATE-{Math.floor(100000 + Math.random() * 900000)}</span>
                  </div>
                </div>
              </div>

              {/* Title Section */}
              <div className="text-center space-y-1 mb-6">
                <h2 className="text-sm font-sans font-black tracking-widest text-black uppercase border-b-2 border-black pb-2">
                  NEXORAX SYSTEM INTEGRATION & COMPLIANCE EXTRACTION RECORD
                </h2>
                <p className="text-[8px] font-mono font-bold text-slate-500 uppercase tracking-widest pt-1">
                  OFFICIAL PROPRIETARY SYSTEM MANDATE DECREED BY THE OFFICE OF THE DIRECTOR
                </p>
              </div>

              {/* Report Narrative Section */}
              <div className="space-y-5 text-slate-800 text-left">
                
                {/* 1. Foreword */}
                <div>
                  <h3 className="text-[10px] font-bold text-black uppercase tracking-wider mb-1.5">
                    1. AUTHORIZATION OF ARCHITECTURAL MANDATE
                  </h3>
                  <p className="text-justify font-sans text-slate-700 leading-relaxed">
                    This document serves as an institutional mandate authorized by Director Lead Director (NexoraX Digital Ltd, Registered Company SC887095). Under active NexoraX direction, we certify the provision of engineered technical systems specifically adapted to meet operational challenges. These parameters establish a high-performance system context designed to run with optimized latency and absolute data compliance.
                  </p>
                </div>

                {/* 2. Parameters List */}
                <div>
                  <h3 className="text-[10px] font-bold text-black uppercase tracking-wider mb-2">
                    2. TARGET SPECIFICATIONS & CORE SOLVER DIAGNOSTICS
                  </h3>
                  <div className="border border-slate-200 rounded-lg overflow-hidden font-mono text-[9px]">
                    <div className="grid grid-cols-12 bg-slate-100 border-b border-slate-200 p-2 font-bold text-slate-700">
                      <div className="col-span-5 uppercase">specification parameter</div>
                      <div className="col-span-7 uppercase border-l border-slate-200 pl-2">applied solver configuration state</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-2">
                      <div className="col-span-12 sm:col-span-5 font-bold uppercase text-slate-600">CLIENT TARGET SECTOR</div>
                      <div className="col-span-12 sm:col-span-7 sm:border-l sm:border-slate-100 sm:pl-2 text-black font-semibold truncate">{(selectedIndustry || customIndustry || "").toUpperCase()}</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-2">
                      <div className="col-span-12 sm:col-span-5 font-bold uppercase text-slate-600">PROPOSED CORE SERVICE</div>
                      <div className="col-span-12 sm:col-span-7 sm:border-l sm:border-slate-100 sm:pl-2 text-black truncate">{serviceType}</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-2">
                      <div className="col-span-12 sm:col-span-5 font-bold uppercase text-slate-600">CRITICAL PAIN RESOLVER</div>
                      <div className="col-span-12 sm:col-span-7 sm:border-l sm:border-slate-100 sm:pl-2 text-black truncate">{painPoint}</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-2">
                      <div className="col-span-12 sm:col-span-5 font-bold uppercase text-slate-600">ENGINEERING NEXORAX STACK</div>
                      <div className="col-span-12 sm:col-span-7 sm:border-l sm:border-slate-100 sm:pl-2 text-black">{currentProposal.stack}</div>
                    </div>
                    <div className="grid grid-cols-12 p-2">
                      <div className="col-span-12 sm:col-span-5 font-bold uppercase text-slate-600">TARGET PERFORMANCE INDEX</div>
                      <div className="col-span-12 sm:col-span-7 sm:border-l sm:border-slate-100 sm:pl-2 text-emerald-700 font-bold uppercase">{currentProposal.roi_projection}</div>
                    </div>
                  </div>
                </div>

                {/* 3. Financial Table */}
                <div>
                  <h3 className="text-[10px] font-bold text-black uppercase tracking-wider mb-2">
                    3. FINANCIAL EXTRACTION PROTOCOLS & REPLICATION DECK
                  </h3>
                  <p className="text-justify font-sans text-slate-700 mb-2 leading-relaxed">
                    Under the strict financial directives of NexoraX Digital Ltd, all corporate payouts must route securely via the Gavel Strike matrix. Remittance metrics are compiled perfectly underneath, accounting for standard tax obligations and global benevolent allocations in continuous synchronization:
                  </p>
                  
                  <div className="border border-slate-200 rounded-lg overflow-hidden font-mono text-[9px]">
                    <div className="grid grid-cols-12 bg-slate-100 border-b border-slate-200 p-2 font-bold text-slate-700">
                      <div className="col-span-7 uppercase">financial allocation directive</div>
                      <div className="col-span-5 border-l border-slate-200 pl-2 text-right uppercase">amount allocated (gbp)</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-2">
                      <div className="col-span-7 font-bold text-slate-600 uppercase">TOTAL SYSTEM VALUE INVESTMENT</div>
                      <div className="col-span-5 border-l border-slate-100 pl-2 text-right text-black font-extrabold">£{currentProposal.investment.toLocaleString()}.00</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-2 bg-amber-500/5">
                      <div className="col-span-7 font-bold text-amber-800 uppercase">5% ARCHITECTURAL INITIATION FEE (RETAINER)</div>
                      <div className="col-span-5 border-l border-slate-100 pl-2 text-right text-amber-900 font-extrabold">£{currentProposal.deposit.toLocaleString()}.00</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-2">
                      <div className="col-span-7 text-slate-600 uppercase pl-2">
                        1% 3% Community Allocation (Corporate Tithe)
                      </div>
                      <div className="col-span-5 border-l border-slate-100 pl-2 text-right text-black font-semibold">£{currentProposal.levy.toLocaleString()}.00</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-1.5 pl-4 text-slate-500 text-[8px] uppercase bg-slate-50">
                      <div className="col-span-8">- Charity 1: Dunfermline Integration Core (33.33%)</div>
                      <div className="col-span-4 text-right">£{currentProposal.charity1Alloc.toFixed(2)}</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-1.5 pl-4 text-slate-500 text-[8px] uppercase bg-slate-50">
                      <div className="col-span-8">- Charity 2: Global Tactical Benevolence (33.33%)</div>
                      <div className="col-span-4 text-right">£{currentProposal.charity2Alloc.toFixed(2)}</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-1.5 pl-4 text-slate-500 text-[8px] uppercase bg-slate-50">
                      <div className="col-span-8">- Charity 3: UK NexoraXty Aid Node (33.33%)</div>
                      <div className="col-span-4 text-right">£{currentProposal.charity3Alloc.toFixed(2)}</div>
                    </div>
                    <div className="grid grid-cols-12 border-b border-slate-100 p-2">
                      <div className="col-span-7 text-slate-600 uppercase pl-2">10% Maintenance & Hosting Fee (NexoraX Gavel Levy)</div>
                      <div className="col-span-5 border-l border-slate-100 pl-2 text-right text-pink-700">£{currentProposal.tax.toLocaleString()}.00</div>
                    </div>
                    <div className="grid grid-cols-12 bg-slate-50 p-2 font-bold text-emerald-800">
                      <div className="col-span-7 uppercase">NET REPLICATED SecurePayment SETTLEMENT DESTINATION</div>
                      <div className="col-span-5 border-l border-slate-200 pl-2 text-right text-emerald-800 font-extrabold text-[10px]">£{currentProposal.netSecurePaymentSettlement.toLocaleString()}.00</div>
                    </div>
                  </div>
                </div>

                {/* 4. Ledger Settlement Instructions */}
                <div>
                  <h3 className="text-[10px] font-bold text-black uppercase tracking-wider mb-1">
                    4. SYSTEM REGISTRATION & BANK REDIRECT DIRECTS
                  </h3>
                  <p className="text-justify font-sans text-slate-700 leading-relaxed text-[10px]">
                    All ultimate ledger outputs are mirrored securely to the Google Drive NexoraX Resource Library via CloudSync-02 background synchronization tasks. Real-time net settlements are designated and cleared natively directly into the verified Secure Payment Provider corporate depository: Sort Code: <strong className="text-black">Verified Payment Configuration</strong> | Registered Account: <strong className="text-black">Secure Billing Provider</strong>.
                  </p>
                </div>

              </div>

              {/* Signature Board Block */}
              <div className="mt-10 pt-8 border-t border-slate-400 grid grid-cols-2 gap-8 text-[9px] font-mono text-slate-600 uppercase text-left">
                <div className="space-y-4">
                  <div className="border-b border-slate-300 pb-1 flex justify-between">
                    <span>AUTHORIZED BY:</span>
                    <span className="text-black font-extrabold">APPROVED SECURE</span>
                  </div>
                  <div className="py-2.5 italic text-[11px] font-serif text-black leading-none font-bold">
                    Lead Director
                  </div>
                  <div className="text-[8px] tracking-wide text-slate-500">
                    Lead Director, Director &bull; NexoraX Digital SC887095
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="border-b border-slate-300 pb-1 flex justify-between">
                    <span>CLIENT ACCEPTANCE HANDSHAKE:</span>
                    <span className="text-slate-400">SIGN LINKED</span>
                  </div>
                  <div className="py-3 border border-slate-200 rounded bg-slate-50 text-center text-[7px] text-slate-400 select-none pb-4">
                    [ ELECTRONIC SECURE SIGNATURE SEAL BLOCK ]
                  </div>
                  <div className="text-[8px] tracking-wide text-slate-500 text-center">
                    DATE: _______________________
                  </div>
                </div>
              </div>

              {/* IP NexoraX copyright label */}
              <div className="mt-8 text-center text-[8px] text-slate-400 uppercase tracking-widest border-t border-slate-200 pt-3">
                PATENT-READINESS PROTECTED ASSETS &copy; {new Date().getFullYear()} NEXORAX DIGITAL LTD. ALL RIGHTS RESERVED.
              </div>

            </div>

            {/* Custom Print Styling element */}
            <style>{`
              @media print {
                body.printing-active-proposal #root > div:not(.print-target-modal) {
                  display: none !important;
                }
                body.printing-active-proposal .print-target-modal {
                  position: absolute !important;
                  left: 0 !important;
                  top: 0 !important;
                  width: 100% !important;
                  height: auto !important;
                  background: white !important;
                  color: black !important;
                  padding: 0 !important;
                  margin: 0 !important;
                  z-index: 9999999 !important;
                }
                .no-print {
                  display: none !important;
                }
              }
            `}</style>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
}
